import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { datadogRum } from '@datadog/browser-rum'
import { withFormik } from 'formik'
import { Try } from 'monapt'
import { pathOr } from 'ramda'
import { compose, withHandlers, withProps, withState } from 'recompose'

import { withTrackClickedHandler } from 'components/trackers'
import { USDFormatter } from 'utils/number/currency'

import withWebView from '../../withWebView'
import {
  BackButtonTapped,
  CancelButtonTapped,
  EditScreenViewed,
  FormScreenViewed,
} from '../segment'
import submitSwitchMutation from '../submitSwitch.mutation.gql'
import {
  employerInfoRows,
  getValidationSchema,
  submittableInfoRows,
} from '../submittable-form'
import { WVProps } from '../types'
import withAddressAutocomplete from '../withAddressAutocomplete'

const enhance = compose<WVProps, {}>(
  withWebView,
  withRouter,
  withTrackClickedHandler(BackButtonTapped),
  withState('showConfirmModal', 'setShowConfirmModal', false),
  withState('editing', 'setEditing', true),
  withState('processing', 'setProcessing', false),
  withState('isFormValid', 'setIsFormValid', ''),
  withHandlers({
    handleNext:
      ({ setEditing, analytics }) =>
      () => {
        setEditing(false)
        analytics.track('Screen Viewed', EditScreenViewed)
      },
  }),
  withFormik({
    validateOnBlur: false,
    validateOnChange: false,
    mapPropsToValues: (props) => ({
      name: pathOr('', ['location', 'state', 'employerName'], props),
    }),
    enableReinitialize: true,
    validationSchema: (props) => {
      return getValidationSchema(pathOr(null, ['location', 'state'], props))
    },
    handleSubmit: (_, { props, setSubmitting }: any) => {
      props.handleNext()
      setSubmitting(false)
    },
  }),
  graphql(submitSwitchMutation, {
    name: 'submitSwitch',
  }),
  withProps<Partial<WVProps>, WVProps>((props) => {
    if (!props.data.loading) {
      return {
        employerInfoRows: employerInfoRows(),
        submittableInfoRows: submittableInfoRows(),
        bankAccountId: pathOr('', ['spendAccounts', 0, 'id'], props.data),
        ssnLastFour: pathOr('', ['profile', 'ssnLastFour'], props.data),
        balance: USDFormatter.format(
          pathOr(0, ['spendAccounts', 0, 'balance', 'value'], props.data),
        ),
        userId: pathOr('', ['user', 'uuid'], props.data),
        employerName: pathOr('', ['location', 'state', 'employerName'], props),
        employerId: pathOr('', ['location', 'state', 'employerId'], props),
        ctaUrl: pathOr('', ['location', 'state', 'ctaUrl'], props),
        clickSwitchTargetId: pathOr(
          '',
          ['location', 'state', 'clickSwitchTargetId'],
          props,
        ),
      }
    } else {
      return {
        employerName: pathOr('', ['location', 'state', 'employerName'], props),
        employerId: pathOr('', ['location', 'state', 'employerId'], props),
        ctaUrl: pathOr('', ['location', 'state', 'ctaUrl'], props),
        clickSwitchTargetId: pathOr(
          '',
          ['location', 'state', 'clickSwitchTargetId'],
          props,
        ),
      }
    }
  }),
  withHandlers({
    handleSelectChange:
      ({ setFieldValue }) =>
      (field, value) => {
        setFieldValue(field, value)
      },
    handleEdit:
      ({ setEditing, analytics }) =>
      () => {
        setEditing(true)
        analytics.track('Screen Viewed', FormScreenViewed)
      },
    handleWebViewBack:
      ({ trackClick, history, location }) =>
      () => {
        Try(() => trackClick())
        location.state && location.state.employerName !== undefined
          ? history.push('/wv/spend/direct-deposit/search')
          : window.location.assign(
              'acorns://event?flow_complete=true&error=false&submit=false',
            )
      },
    handleWebViewExit:
      ({ history, analytics, employerName, location }) =>
      () => {
        analytics.track('Button Tapped', CancelButtonTapped(employerName))

        location.state && location.state.employerName !== undefined
          ? history.push('/wv/spend/direct-deposit/search')
          : window.location.assign(
              'acorns://event?flow_complete=true&error=false&submit=false',
            )
      },
    handleSubmitSwitch:
      ({
        submitSwitch,
        bankAccountId,
        employerId,
        values,
        setProcessing,
        analytics,
      }) =>
      () => {
        setProcessing(true)
        submitSwitch({
          variables: {
            employerId,
            bankAccountId,
            sessionId: analytics.getContext().sessionId,
            employerLocationAddress: {
              street1: values.address1 || '',
              street2: values.address2 || '',
              city: values.city || '',
              state: values.state || '',
              zipCode: values.zip || '',
              country: 'US',
            },
          },
        }).then(
          () => {
            setProcessing(false)
            window.location.assign(
              'acorns://event?flow_complete=true&error=false&submit=true',
            )
          },
          (err) => {
            datadogRum.addError(err)
            setProcessing(false)
            window.location.assign(
              'acorns://event?flow_complete=true&error=true&submit=false',
            )
          },
        )
      },
  }),
  withAddressAutocomplete,
)

export default enhance
